import { useEffect, useState } from 'react';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { ThemeButton } from '@shared/ui/buttons';
import Note from '@shared/ui/messages/Note';
import { Status, ListData } from '@shared/ui/parts';
import BlockLoader from '@shared/ui/messages/BlockLoader';
import { formatWalletAddress, formatShareUrlLink } from '@utils/helpers';
import { formatTimeRemaining, formatDateTime } from '@utils/date-helpers';
import { useCountdownTimer } from '@utils/hooks/useCountdownTimer';
import { paymentStatuses } from '@utils/statuses';
import QRCode from 'react-qr-code';
import { CopyToClipWhisper } from '@shared/ui/messages/CopyToClipWhisper';
import { CopyThemeButtonIcon } from '@shared/ui/messages/CopyThemeButtonIcon';
import { useOrder } from '@app/api/cards/useOrder';
import { getCryptoName } from '@utils/balance-helpers';
import { currencySymbols } from '@utils/static';
import { icons } from '@shared/static/icons';
import { baseFrontendUrl } from '@app/api/axios';
import { useTranslation } from 'react-i18next';
import { theme } from '@utils/libs/theme';
import useMediaQuery from '@mui/material/useMediaQuery';
import NestedConfirmDialog from '@shared/ui/messages/NestedConfirmDialog';
import { fetchBalances } from '@app/store/slices/balancesSlice';
import { useDispatch } from 'react-redux';

function OrderAmount({ order }) {
  const { t } = useTranslation();

  return (
    <>
      <p className="body-18m">{t('top_up_amount')}</p>
      <div className="topUpOrder__balance">
        <p>
          {order?.amount_from} {getCryptoName(order?.currency_from)}
        </p>
        <CopyThemeButtonIcon value={order?.amount_from} />
      </div>
    </>
  );
}

function OrderInfo({ order, isWaiting }) {
  const { t } = useTranslation();

  if (!isWaiting) return null;

  return (
    <div className="topUpOrder__info">
      <div className="topUpOrder__network body-18m">
        {icons[getCryptoName(order?.currency_from)]}
        <p>{order?.currency_from}</p>
      </div>
      <div className="topUpOrder__qr">
        {order?.address && <QRCode value={order?.address} />}
      </div>
      <div className="topUpOrder__links">
        <div className="topUpOrder__links-item">
          <p className="secondary-text">{t('wallet_address')}</p>
          <CopyToClipWhisper
            value={order?.address}
            display={formatWalletAddress(order?.address)}
          />
        </div>
        <div className="topUpOrder__links-item">
          <p className="secondary-text">{t('share_link')}</p>
          <CopyToClipWhisper
            value={`${baseFrontendUrl}/order/${order?.order_id}`}
            display={formatShareUrlLink(
              `${baseFrontendUrl}/order/${order?.order_id}`,
            )}
          />
        </div>
      </div>
    </div>
  );
}

function OrderNote({ isWaiting, isCompleted, isMobile }) {
  const { t } = useTranslation();

  if (isCompleted) return null;

  return (
    <Note
      marginBottom
      marginTop={isMobile}
      type={isWaiting ? 'warning' : 'error'}
      title={t('important_choose_correct_network')}
      singleLine={!isWaiting}
      content={t(
        isWaiting ? 'selecting_wrong_network_note' : 'cancelled_order_note',
      )}
    />
  );
}

export default function TopUpOrderDialog({
  close,
  setSubtitle,
  setTitleAction,
  data,
}) {
  const { t } = useTranslation();
  const [order, setOrder] = useState(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const timeRemaining = useCountdownTimer(order?.expired_at);
  const { fetchOrderById } = useOrder();
  const [isConfirm, setIsConfirm] = useState(false);
  const { cancelOrder } = useOrder();
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const status = order?.status;
  const statusLabel = paymentStatuses[status];

  const isCanceled = statusLabel === 'canceled';
  const isWaiting = statusLabel === 'waiting';
  const isCompleted = statusLabel === 'completed';

  const isFinished = isCanceled || isCompleted;

  useEffect(() => {
    if (!data) return;
    setOrder(null);
    fetchOrderById(data, setOrder);
  }, [data]);

  useEffect(() => {
    if (!data || isFinished) return;

    const intervalId = setInterval(() => {
      fetchOrderById(data, setOrder);
    }, 3000);

    return () => clearInterval(intervalId);
  }, [data, isFinished]);

  useEffect(() => {
    if (!isFinished) return;
    dispatch(fetchBalances());
    setIsConfirm(false);
  }, [isFinished, dispatch]);

  const listData = !order
    ? []
    : [
        {
          label: t('top_up_fee'),
          value: `${order.bank_to?.top_up_fee || ''} %`,
        },
        {
          label: t('deposit_amount'),
          value: `${order.amount_to || ''} ${currencySymbols[order.currency_to] || ''}`,
        },
        {
          label: t('date_time'),
          value: formatDateTime(order.created_at),
        },
        {
          label: t('order_id'),
          value: order.order_id || '',
          isCopy: true,
        },
      ];

  useEffect(() => {
    if (!order) return;
    setSubtitle(<Status data={status} type="payments" />);
    if (isCanceled || isCompleted) {
      setTitleAction(null);
      return;
    }
    setTitleAction(
      <ThemeButton
        appearance="secondary"
        startIcon="delete"
        onClick={() => setIsConfirm(true)}
      >
        {t('cancel')}
      </ThemeButton>,
    );
  }, [order, status, isCanceled, isCompleted, setSubtitle, setTitleAction, t]);

  async function onCancelOrder() {
    setIsLoading(true);
    try {
      const res = await cancelOrder(order?.order_id);
      if (res) {
        setIsConfirm(false);
        fetchOrderById(data, setOrder);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  if (order === null) {
    return (
      <div>
        <DialogContent>
          <BlockLoader />
        </DialogContent>
      </div>
    );
  }

  return (
    <>
      <DialogContent>
        {!isMobile && (
          <OrderNote
            isWaiting={isWaiting}
            isCompleted={isCompleted}
            isMobile={isMobile}
          />
        )}
        <div className="topUpOrder">
          {!isMobile && (
            <>
              <div>
                <OrderAmount order={order} />
                <ListData listData={listData} />
              </div>
              <OrderInfo order={order} isWaiting={isWaiting} />
            </>
          )}
          {isMobile && (
            <>
              <OrderAmount order={order} />
              <OrderInfo order={order} isWaiting={isWaiting} />
              <OrderNote
                isWaiting={isWaiting}
                isCompleted={isCompleted}
                isMobile={isMobile}
              />
              <ListData listData={listData} />
            </>
          )}
        </div>
      </DialogContent>
      {isWaiting && (
        <DialogActions>
          <div className="topUpOrder__valid">
            <p className="secondary-text">{t('valid_for')}:</p>
            <p className="title-h3m">{formatTimeRemaining(timeRemaining)}</p>
          </div>
        </DialogActions>
      )}
      <NestedConfirmDialog
        open={isConfirm}
        close={() => setIsConfirm(false)}
        title={t('cancel_order')}
        text={t('cancel_order_text')}
        onSubmit={onCancelOrder}
        loading={isLoading}
      />
    </>
  );
}
